import React, { useState, useEffect } from 'react';
import MemberWallet from './MemberWallet';
import axios from 'axios';
import { useAuth } from '../App';
import { getApiUrl } from '../config/api';
import { createPortal } from 'react-dom';

const CustomerList = () => {
  const { hasPermission } = useAuth();

  // State
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Context menu state
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, customer: null });

  // Member wallet
  const [showMemberWallet, setShowMemberWallet] = useState(null);

  // Adjust credit
  const [showAdjustCreditModal, setShowAdjustCreditModal] = useState(false);
  const [adjustCreditUserId, setAdjustCreditUserId] = useState(null);
  const [adjustCreditAmount, setAdjustCreditAmount] = useState('');
  const [adjustCreditReason, setAdjustCreditReason] = useState('');
  const [adjustCreditLoading, setAdjustCreditLoading] = useState(false);
  const [adjustCreditError, setAdjustCreditError] = useState(null);

  // Create / Edit
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    company_name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    website: '',
    notes: '',
    bank_name: '',
    bank_account_number: '',
    bank_account_holder: '',
    bank_branch: '',
    custom_json_data: '{}',
    api_password: ''
  });

  // JSON Editor state
  const [jsonError, setJsonError] = useState('');

  // API Management state
  const [showApiModal, setShowApiModal] = useState(false);
  const [selectedCustomerForApi, setSelectedCustomerForApi] = useState(null);
  const [apiInfo, setApiInfo] = useState(null);

  // Reset password
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordCustomer, setResetPasswordCustomer] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Fetch customers
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await axios.get(getApiUrl('/customers'));
        setCustomers(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  // Permission guard
  if (!hasPermission('customer', 'read')) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">🔒</div>
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Không có quyền truy cập</h2>
          <p className="text-gray-500">Bạn không có quyền xem danh sách khách hàng.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Handlers
  const formatDate = date => new Date(date).toLocaleDateString();

  const openEditModal = customer => {
    setEditingCustomer(customer);
    setFormData({
      company_name: customer.company_name || '',
      contact_person: customer.contact_person || '',
      email: customer.email || '',
      phone: customer.phone || '',
      address: customer.address || '',
      website: customer.website || '',
      notes: customer.notes || '',
      bank_name: customer.bank_name || '',
      bank_account_number: customer.bank_account_number || '',
      bank_account_holder: customer.bank_account_holder || '',
      bank_branch: customer.bank_branch || '',
      custom_json_data: JSON.stringify(customer.custom_json_data || {}, null, 2),
      api_password: customer.api_password || ''
    });
    setJsonError('');
    setShowCreateModal(false);
  };

  const handleDelete = async id => {
    if (!window.confirm('Bạn có chắc muốn xóa khách hàng này?')) return;
    try {
      await axios.delete(getApiUrl(`/customers/${id}`));
      setCustomers(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      console.error(err);
      alert('Xóa thất bại');
    }
  };

  const handleResetPassword = async customer => {
    try {
      const res = await axios.post(getApiUrl(`/customers/${customer.id}/reset-password`));
      setNewPassword(res.data.newPassword);
      setResetPasswordCustomer(res.data);
      setShowResetPasswordModal(true);
    } catch (err) {
      console.error(err);
      alert('Reset password thất bại');
    }
  };

  const handleCreateSubmit = async e => {
    e.preventDefault();
    
    // Validate JSON
    try {
      if (formData.custom_json_data) {
        JSON.parse(formData.custom_json_data);
      }
    } catch (error) {
      setJsonError('JSON không hợp lệ');
      return;
    }

    try {
      const submitData = { ...formData };
      if (formData.custom_json_data) {
        submitData.custom_json_data = JSON.parse(formData.custom_json_data);
      }
      
      const res = await axios.post(getApiUrl('/customers'), submitData);
      setCustomers(prev => [res.data, ...prev]);
      setShowCreateModal(false);
      resetForm();
    } catch (err) {
      console.error(err);
      alert('Tạo khách hàng thất bại');
    }
  };

  const handleUpdateSubmit = async e => {
    e.preventDefault();
    
    // Validate JSON
    try {
      if (formData.custom_json_data) {
        JSON.parse(formData.custom_json_data);
      }
    } catch (error) {
      setJsonError('JSON không hợp lệ');
      return;
    }

    try {
      const submitData = { ...formData };
      if (formData.custom_json_data) {
        submitData.custom_json_data = JSON.parse(formData.custom_json_data);
      }

      const res = await axios.put(getApiUrl(`/customers/${editingCustomer.id}`), submitData);
      setCustomers(prev => prev.map(c => (c.id === editingCustomer.id ? res.data : c)));
      setEditingCustomer(null);
      resetForm();
    } catch (err) {
      console.error(err);
      alert('Cập nhật thất bại');
    }
  };

  const resetForm = () => {
    setFormData({ 
      company_name: '', 
      contact_person: '', 
      email: '', 
      phone: '', 
      address: '', 
      website: '',
      notes: '',
      bank_name: '',
      bank_account_number: '',
      bank_account_holder: '',
      bank_branch: '',
      custom_json_data: '{}',
      api_password: ''
    });
    setJsonError('');
  };

  const handleGenerateApiPassword = async (customerId) => {
    try {
      const res = await axios.post(getApiUrl(`/customers/${customerId}/generate-api-password`));
      setApiInfo(res.data);
      setCustomers(prev => 
        prev.map(c => 
          c.id === customerId 
            ? { ...c, api_password: res.data.api_password }
            : c
        )
      );
      alert('API Password đã được tạo thành công!');
    } catch (err) {
      console.error(err);
      alert('Tạo API Password thất bại');
    }
  };

  const handleShowApiModal = (customer) => {
    setSelectedCustomerForApi(customer);
    setShowApiModal(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Đã sao chép vào clipboard!');
    });
  };

  const handleAdjustCredit = async e => {
    e.preventDefault();
    setAdjustCreditLoading(true);
    setAdjustCreditError(null);
    try {
      await axios.post(
        getApiUrl(`/wallet/member/${adjustCreditUserId}/adjust-credit`),
        { amount: parseFloat(adjustCreditAmount), reason: adjustCreditReason }
      );
      setShowAdjustCreditModal(false);
      setAdjustCreditAmount('');
      setAdjustCreditReason('');
      // reload wallet if open
      if (showMemberWallet === adjustCreditUserId) {
        setShowMemberWallet(null);
        setTimeout(() => setShowMemberWallet(adjustCreditUserId), 100);
      }
      alert('Điều chỉnh số dư thành công!');
    } catch (err) {
      console.error(err);
      setAdjustCreditError(err.response?.data?.detail || 'Lỗi điều chỉnh số dư');
    } finally {
      setAdjustCreditLoading(false);
    }
  };

  // Filter customers
  const displayed = customers.filter(c =>
    [c.company_name, c.contact_person, c.email, c.phone]
      .some(field => field?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <>
      <div className="space-y-6">
        {/* Header & Search */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý khách hàng</h1>
            <p className="text-gray-600">Danh sách {customers.length} khách hàng</p>
          </div>
          {hasPermission('customer', 'write') && (
            <button onClick={() => { setShowCreateModal(true); setEditingCustomer(null); }} className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              Thêm khách hàng
            </button>
          )}
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <input type="text" placeholder="Tìm kiếm..." className="w-full px-4 py-2 border rounded" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Công ty</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Liên hệ</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Thông tin</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ngày tạo</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Thao tác</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayed.map(customer => (
                <tr key={customer.id} className="hover:bg-gray-50" onContextMenu={e => { e.preventDefault(); setContextMenu({ visible: true, x: e.clientX, y: e.clientY, customer }); }}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{customer.company_name}</div>
                    {customer.address && <div className="text-sm text-gray-500 truncate max-w-xs">{customer.address}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{customer.contact_person}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    {customer.email && <div>{customer.email}</div>}
                    {customer.phone && <div className="text-gray-500">{customer.phone}</div>}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(customer.created_at)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button onClick={() => openEditModal(customer)} className="text-blue-600 hover:underline mr-2">Sửa</button>
                    {hasPermission('customer','write') && <button onClick={() => handleDelete(customer.id)} className="text-red-600 hover:underline">Xóa</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {displayed.length === 0 && (
            <div className="text-center py-8 text-gray-500">Không tìm thấy khách hàng phù hợp</div>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu.visible && createPortal(
        <div style={{ position:'fixed', top:contextMenu.y,left:contextMenu.x,zIndex:1000,background:'#fff',boxShadow:'0 2px 8px rgba(0,0,0,0.15)',borderRadius:8 }} onClick={()=>setContextMenu({ ...contextMenu, visible:false })}>
          <button className="block px-4 py-2 hover:bg-gray-100 w-full text-left" onClick={()=>{ openEditModal(contextMenu.customer); setContextMenu({...contextMenu,visible:false}); }}>Xem/Chỉnh sửa</button>
          <button className="block px-4 py-2 hover:bg-gray-100 w-full text-left" onClick={()=>{ handleDelete(contextMenu.customer.id); setContextMenu({...contextMenu,visible:false}); }}>Xóa</button>
          <button className="block px-4 py-2 hover:bg-gray-100 w-full text-left" onClick={()=>{ handleResetPassword(contextMenu.customer); setContextMenu({...contextMenu,visible:false}); }}>Reset Password</button>
          <button className="block px-4 py-2 hover:bg-gray-100 w-full text-left" onClick={()=>{ setShowMemberWallet(contextMenu.customer.id); setContextMenu({...contextMenu,visible:false}); }}>Xem ví thành viên</button>
          <button className="block px-4 py-2 hover:bg-gray-100 w-full text-left" onClick={()=>{ setAdjustCreditUserId(contextMenu.customer.id); setShowAdjustCreditModal(true); setContextMenu({...contextMenu,visible:false}); }}>Điều chỉnh số dư</button>
          <button className="block px-4 py-2 hover:bg-gray-100 w-full text-left" onClick={()=>{ handleShowApiModal(contextMenu.customer); setContextMenu({...contextMenu,visible:false}); }}>Quản lý API</button>
        </div>, document.body
      )}

      {/* Member Wallet Modal */}
      {showMemberWallet && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowMemberWallet(null)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-lg w-full"
            onClick={e => e.stopPropagation()}
          >
            <MemberWallet userId={showMemberWallet} onClose={()=>setShowMemberWallet(null)} />
          </div>
        </div>
      )}

      {/* Adjust Credit Modal */}
      {showAdjustCreditModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => setShowAdjustCreditModal(false)}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <h2 className="text-lg font-bold mb-4">Điều chỉnh số dư thành viên #{adjustCreditUserId}</h2>
            <form onSubmit={handleAdjustCredit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1">Số tiền (+/-)</label>
                <input type="number" step="0.01" required className="w-full border px-3 py-2" value={adjustCreditAmount} onChange={e=>setAdjustCreditAmount(e.target.value)} />
              </div>
              <div>
                <label className="block text-sm mb-1">Lý do</label>
                <input type="text" required className="w-full border px-3 py-2" value={adjustCreditReason} onChange={e=>setAdjustCreditReason(e.target.value)} />
              </div>
              {adjustCreditError && <div className="text-red-600">{adjustCreditError}</div>}
              <div className="flex justify-end space-x-2">
                <button type="button" onClick={()=>setShowAdjustCreditModal(false)} className="px-4 py-2 bg-gray-200 rounded">Hủy</button>
                <button type="submit" disabled={adjustCreditLoading} className="px-4 py-2 bg-blue-600 text-white rounded">{adjustCreditLoading? 'Đang xử lý...':'Xác nhận'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => { setShowCreateModal(false); resetForm(); }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">Thêm khách hàng mới</h3>
            <form onSubmit={handleCreateSubmit} className="space-y-6">
              
              {/* Basic Information */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Thông tin cơ bản</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Tên công ty *</label>
                    <input type="text" required value={formData.company_name} onChange={e=>setFormData({...formData,company_name:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Người liên hệ</label>
                    <input type="text" value={formData.contact_person} onChange={e=>setFormData({...formData,contact_person:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Email</label>
                    <input type="email" value={formData.email} onChange={e=>setFormData({...formData,email:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Số điện thoại</label>
                    <input type="tel" value={formData.phone} onChange={e=>setFormData({...formData,phone:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Website</label>
                    <input type="url" value={formData.website} onChange={e=>setFormData({...formData,website:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-1">Địa chỉ</label>
                    <textarea rows="2" value={formData.address} onChange={e=>setFormData({...formData,address:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                </div>
              </div>

              {/* Bank Information */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Thông tin ngân hàng</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Tên ngân hàng</label>
                    <input type="text" value={formData.bank_name} onChange={e=>setFormData({...formData,bank_name:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Số tài khoản</label>
                    <input type="text" value={formData.bank_account_number} onChange={e=>setFormData({...formData,bank_account_number:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Tên chủ tài khoản</label>
                    <input type="text" value={formData.bank_account_holder} onChange={e=>setFormData({...formData,bank_account_holder:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Chi nhánh</label>
                    <input type="text" value={formData.bank_branch} onChange={e=>setFormData({...formData,bank_branch:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                </div>
              </div>

              {/* Custom JSON Data */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Dữ liệu JSON tùy chỉnh</h4>
                <div>
                  <label className="block text-sm mb-1">JSON Data</label>
                  <textarea 
                    rows="6" 
                    value={formData.custom_json_data} 
                    onChange={e=>{
                      setFormData({...formData,custom_json_data:e.target.value});
                      setJsonError('');
                    }} 
                    className={`w-full border px-3 py-2 rounded font-mono text-sm ${jsonError ? 'border-red-500' : ''}`}
                    placeholder='{"key": "value"}'
                  />
                  {jsonError && <p className="text-red-500 text-sm mt-1">{jsonError}</p>}
                </div>
              </div>

              {/* API Password */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">API Access</h4>
                <div>
                  <label className="block text-sm mb-1">API Password (tùy chọn)</label>
                  <input type="text" value={formData.api_password} onChange={e=>setFormData({...formData,api_password:e.target.value})} className="w-full border px-3 py-2 rounded font-mono" placeholder="Để trống để tạo sau" />
                  <p className="text-gray-500 text-xs mt-1">Có thể để trống và tạo sau thông qua menu context</p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Ghi chú</h4>
                <div>
                  <textarea rows="3" value={formData.notes} onChange={e=>setFormData({...formData,notes:e.target.value})} className="w-full border px-3 py-2 rounded" placeholder="Ghi chú về khách hàng..." />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <button type="button" onClick={()=>{setShowCreateModal(false);resetForm();}} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Hủy</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Tạo khách hàng</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingCustomer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => { setEditingCustomer(null); resetForm(); }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-medium mb-4">Chỉnh sửa khách hàng: {editingCustomer.company_name}</h3>
            <form onSubmit={handleUpdateSubmit} className="space-y-6">
              
              {/* Basic Information */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Thông tin cơ bản</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Tên công ty *</label>
                    <input type="text" required value={formData.company_name} onChange={e=>setFormData({...formData,company_name:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Người liên hệ</label>
                    <input type="text" value={formData.contact_person} onChange={e=>setFormData({...formData,contact_person:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Email</label>
                    <input type="email" value={formData.email} onChange={e=>setFormData({...formData,email:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Số điện thoại</label>
                    <input type="tel" value={formData.phone} onChange={e=>setFormData({...formData,phone:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Website</label>
                    <input type="url" value={formData.website} onChange={e=>setFormData({...formData,website:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm mb-1">Địa chỉ</label>
                    <textarea rows="2" value={formData.address} onChange={e=>setFormData({...formData,address:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                </div>
              </div>

              {/* Bank Information */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Thông tin ngân hàng</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Tên ngân hàng</label>
                    <input type="text" value={formData.bank_name} onChange={e=>setFormData({...formData,bank_name:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Số tài khoản</label>
                    <input type="text" value={formData.bank_account_number} onChange={e=>setFormData({...formData,bank_account_number:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Tên chủ tài khoản</label>
                    <input type="text" value={formData.bank_account_holder} onChange={e=>setFormData({...formData,bank_account_holder:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Chi nhánh</label>
                    <input type="text" value={formData.bank_branch} onChange={e=>setFormData({...formData,bank_branch:e.target.value})} className="w-full border px-3 py-2 rounded" />
                  </div>
                </div>
              </div>

              {/* Custom JSON Data */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Dữ liệu JSON tùy chỉnh</h4>
                <div>
                  <label className="block text-sm mb-1">JSON Data</label>
                  <textarea 
                    rows="6" 
                    value={formData.custom_json_data} 
                    onChange={e=>{
                      setFormData({...formData,custom_json_data:e.target.value});
                      setJsonError('');
                    }} 
                    className={`w-full border px-3 py-2 rounded font-mono text-sm ${jsonError ? 'border-red-500' : ''}`}
                    placeholder='{"key": "value"}'
                  />
                  {jsonError && <p className="text-red-500 text-sm mt-1">{jsonError}</p>}
                </div>
              </div>

              {/* API Password */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">API Access</h4>
                <div>
                  <label className="block text-sm mb-1">API Password</label>
                  <div className="flex">
                    <input 
                      type="text" 
                      value={formData.api_password} 
                      onChange={e=>setFormData({...formData,api_password:e.target.value})} 
                      className="flex-1 border px-3 py-2 rounded-l font-mono" 
                      placeholder="Chưa có API password"
                    />
                    <button
                      type="button"
                      onClick={() => handleGenerateApiPassword(editingCustomer.id)}
                      className="px-4 py-2 bg-green-600 text-white rounded-r hover:bg-green-700"
                    >
                      Tạo mới
                    </button>
                  </div>
                  <p className="text-gray-500 text-xs mt-1">Dùng để truy cập API từ bên ngoài</p>
                </div>
              </div>

              {/* Notes */}
              <div>
                <h4 className="font-medium text-gray-900 mb-3">Ghi chú</h4>
                <div>
                  <textarea rows="3" value={formData.notes} onChange={e=>setFormData({...formData,notes:e.target.value})} className="w-full border px-3 py-2 rounded" placeholder="Ghi chú về khách hàng..." />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <button type="button" onClick={()=>{setEditingCustomer(null);resetForm();}} className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300">Đóng</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Cập nhật</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && resetPasswordCustomer && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => { setShowResetPasswordModal(false); setResetPasswordCustomer(null); setNewPassword(''); }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-md w-full"
            onClick={e => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-2">
                🔑
              </div>
              <h3 className="text-lg font-medium text-gray-900">Password đã được reset</h3>
            </div>
            <div className="bg-gray-50 p-4 rounded mb-4">
              <p><strong>Email:</strong> {resetPasswordCustomer.email}</p>
              <p><strong>Password mới:</strong></p>
              <code className="block bg-white p-2 rounded break-all mt-1">{resetPasswordCustomer.new_password}</code>
            </div>
            <div className="flex justify-center space-x-2">
              <button onClick={()=>{navigator.clipboard.writeText(resetPasswordCustomer.new_password);alert('Copied!');}} className="px-4 py-2 bg-blue-600 text-white rounded">Sao chép</button>
              <button onClick={()=>{setShowResetPasswordModal(false);setResetPasswordCustomer(null);setNewPassword('');}} className="px-4 py-2 bg-gray-200 rounded">Đóng</button>
            </div>
          </div>
        </div>
      )}

      {/* API Management Modal */}
      {showApiModal && selectedCustomerForApi && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={() => { setShowApiModal(false); setSelectedCustomerForApi(null); setApiInfo(null); }}
        >
          <div
            className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Quản lý API - {selectedCustomerForApi.company_name}
              </h3>
              <button
                onClick={() => { setShowApiModal(false); setSelectedCustomerForApi(null); setApiInfo(null); }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {/* Current API Status */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">Trạng thái API hiện tại:</h4>
              {selectedCustomerForApi.api_password ? (
                <div className="bg-green-50 p-3 rounded-md">
                  <div className="flex items-center text-green-700">
                    <span className="mr-2">✅</span>
                    API đã được kích hoạt
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 p-3 rounded-md">
                  <div className="flex items-center text-yellow-700">
                    <span className="mr-2">⚠️</span>
                    API chưa được kích hoạt
                  </div>
                </div>
              )}
            </div>

            {/* API Password Section */}
            {selectedCustomerForApi.api_password && (
              <div className="mb-6">
                <h4 className="font-medium mb-2">API Password:</h4>
                <div className="flex">
                  <input
                    type="text"
                    value={selectedCustomerForApi.api_password}
                    readOnly
                    className="flex-1 p-2 border border-gray-300 rounded-l-md bg-gray-50 font-mono text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(selectedCustomerForApi.api_password)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-r-md hover:bg-gray-700"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* API URL Section */}
            {selectedCustomerForApi.api_password && (
              <div className="mb-6">
                <h4 className="font-medium mb-2">API URL:</h4>
                <div className="flex">
                  <input
                    type="text"
                    value={`${window.location.origin}/customers/api/${selectedCustomerForApi.id}/data`}
                    readOnly
                    className="flex-1 p-2 border border-gray-300 rounded-l-md bg-gray-50 font-mono text-sm"
                  />
                  <button
                    onClick={() => copyToClipboard(`${window.location.origin}/customers/api/${selectedCustomerForApi.id}/data`)}
                    className="px-4 py-2 bg-gray-600 text-white rounded-r-md hover:bg-gray-700"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Usage Example */}
            {selectedCustomerForApi.api_password && (
              <div className="mb-6">
                <h4 className="font-medium mb-2">Ví dụ sử dụng cURL:</h4>
                <div className="flex">
                  <code className="flex-1 bg-gray-100 p-2 rounded-l text-sm break-all">
                    {`curl -H "X-API-Password: ${selectedCustomerForApi.api_password}" ${window.location.origin}/customers/api/${selectedCustomerForApi.id}/data`}
                  </code>
                  <button
                    onClick={() => copyToClipboard(`curl -H "X-API-Password: ${selectedCustomerForApi.api_password}" ${window.location.origin}/customers/api/${selectedCustomerForApi.id}/data`)}
                    className="px-2 py-2 bg-gray-600 text-white rounded-r text-xs hover:bg-gray-700"
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}

            {/* Current JSON Data Preview */}
            <div className="mb-6">
              <h4 className="font-medium mb-2">Dữ liệu JSON hiện tại:</h4>
              <div className="bg-gray-100 p-3 rounded-md">
                <pre className="text-sm overflow-x-auto">
                  {JSON.stringify(selectedCustomerForApi.custom_json_data || {}, null, 2)}
                </pre>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between">
              <button
                onClick={() => handleGenerateApiPassword(selectedCustomerForApi.id)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {selectedCustomerForApi.api_password ? 'Tạo lại API Password' : 'Tạo API Password'}
              </button>
              
              <button
                onClick={() => { setShowApiModal(false); setSelectedCustomerForApi(null); setApiInfo(null); }}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Đóng
              </button>
            </div>

            {/* API Info Display */}
            {apiInfo && (
              <div className="mt-4 p-4 bg-green-50 rounded-md">
                <h5 className="font-medium text-green-800 mb-2">✅ API Password mới đã được tạo!</h5>
                <div className="space-y-2">
                  <div>
                    <label className="text-sm font-medium text-green-700">Password:</label>
                    <div className="flex">
                      <code className="flex-1 bg-white p-1 rounded-l text-sm">{apiInfo.api_password}</code>
                      <button
                        onClick={() => copyToClipboard(apiInfo.api_password)}
                        className="px-2 py-1 bg-green-600 text-white rounded-r text-xs"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CustomerList;
